import { classifyEmail } from './emailClassification';
import { addToSessionHistory } from './sessionHistory';
import { trackGoogleAdsConversion, GOOGLE_ADS_CONVERSIONS } from './googleAdsConversions';

// Note: Window._paq and dataLayer are already declared in hooks/useAnalytics.ts
// We use the existing global types

// ==================== CORE HELPER ====================
export function fireMicroConversion(
  eventName: string,
  email: string,
  baseValue: number,
  metadata?: Record<string, unknown>
): void {
  const emailType = classifyEmail(email);
  const value = emailType === 'business' ? baseValue : 0;
  const fullEventName = `${eventName}_${emailType}`;

  // Push to GTM dataLayer
  if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: fullEventName,
      email_type: emailType,
      value,
      currency: 'USD',
      ...metadata,
    });
  }

  // Push to Matomo
  if (typeof window !== 'undefined' && window._paq) {
    const paq = window._paq;
    if (Array.isArray(paq)) {
      paq.push(['trackEvent', 'micro_conversion', eventName, emailType, value]);
    } else if (typeof paq.push === 'function') {
      paq.push(['trackEvent', 'micro_conversion', eventName, emailType, value]);
    }
  }

  // GA4
  if (typeof window !== 'undefined') {
    window.gtag?.('event', fullEventName, {
      event_category: 'micro_conversion',
      email_type: emailType,
      value,
    });
  }

  addToSessionHistory(fullEventName);
}

// ==================== VIDEO EVENTS ====================
const VIDEO_VALUES = {
  teaser: { play: 2, '25': 3, '50': 5, '75': 8, complete: 12 },
  demo: { play: 10, '5min': 15, '15min': 25, '25min': 35, complete: 40 },
} as const;

type TeaserMilestone = keyof typeof VIDEO_VALUES.teaser;
type DemoMilestone = keyof typeof VIDEO_VALUES.demo;

export function fireVideoEvent(videoType: 'teaser' | 'demo', milestone: string | number): void {
  const eventName = `video_${videoType}_${milestone}`;
  const milestoneKey = String(milestone);

  let value = 0;
  if (videoType === 'teaser' && milestoneKey in VIDEO_VALUES.teaser) {
    value = VIDEO_VALUES.teaser[milestoneKey as TeaserMilestone];
  } else if (videoType === 'demo' && milestoneKey in VIDEO_VALUES.demo) {
    value = VIDEO_VALUES.demo[milestoneKey as DemoMilestone];
  }

  if (typeof window !== 'undefined') {
    window.dataLayer?.push({ event: eventName, value, currency: 'USD' });

    const paq = window._paq;
    if (paq) {
      if (Array.isArray(paq)) {
        paq.push(['trackEvent', 'video', String(milestone), videoType, value]);
      } else if (typeof paq.push === 'function') {
        paq.push(['trackEvent', 'video', String(milestone), videoType, value]);
      }
    }

    // GA4
    window.gtag?.('event', eventName, {
      event_category: 'video',
      video_type: videoType,
      milestone: milestoneKey,
      value,
    });

    // Fire Google Ads conversions for demo video milestones
    if (videoType === 'demo') {
      const milestoneToConversion: Record<
        string,
        { key: keyof typeof GOOGLE_ADS_CONVERSIONS; value: number }
      > = {
        play: { key: 'videoDemoPlay', value: 10 },
        '5min': { key: 'videoDemo5min', value: 15 },
        '15min': { key: 'videoDemo15min', value: 25 },
        complete: { key: 'videoDemoComplete', value: 40 },
      };

      const conversion = milestoneToConversion[milestoneKey];
      if (conversion) {
        trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS[conversion.key], conversion.value);
      }
    }
  }

  addToSessionHistory(eventName);
}

// ==================== SCROLL EVENTS ====================
const SCROLL_VALUES: Record<number, number> = { 25: 1, 50: 3, 75: 5, 100: 8 };

export function fireScrollEvent(pageName: string, threshold: number): void {
  const eventName = `scroll_${threshold}`;
  const value = SCROLL_VALUES[threshold] || 0;

  if (typeof window !== 'undefined') {
    window.dataLayer?.push({ event: eventName, page: pageName, value, currency: 'USD' });

    const paq = window._paq;
    if (paq) {
      if (Array.isArray(paq)) {
        paq.push(['trackEvent', 'engagement', 'scroll', `${pageName}:${threshold}`, value]);
      } else if (typeof paq.push === 'function') {
        paq.push(['trackEvent', 'engagement', 'scroll', `${pageName}:${threshold}`, value]);
      }
    }

    // GA4
    window.gtag?.('event', eventName, { event_category: 'engagement', page: pageName, value });
  }

  addToSessionHistory(`${eventName}_${pageName}`);
}

// ==================== TIME EVENTS ====================
const TIME_VALUES: Record<number, number> = { 30: 1, 60: 3, 120: 5, 300: 10 };

export function fireTimeEvent(pageName: string, seconds: number): void {
  const label = seconds >= 60 ? `${Math.floor(seconds / 60)}min` : `${seconds}s`;
  const eventName = `time_${label}`;
  const value = TIME_VALUES[seconds] || 0;

  if (typeof window !== 'undefined') {
    window.dataLayer?.push({ event: eventName, page: pageName, value, currency: 'USD' });

    const paq = window._paq;
    if (paq) {
      if (Array.isArray(paq)) {
        paq.push(['trackEvent', 'engagement', 'time', `${pageName}:${label}`, value]);
      } else if (typeof paq.push === 'function') {
        paq.push(['trackEvent', 'engagement', 'time', `${pageName}:${label}`, value]);
      }
    }

    // GA4
    window.gtag?.('event', eventName, { event_category: 'engagement', page: pageName, value });
  }

  addToSessionHistory(`${eventName}_${pageName}`);
}

// ==================== SESSION DEPTH EVENTS ====================
export function fireSessionEvent(eventName: string, value: number): void {
  if (typeof window !== 'undefined') {
    window.dataLayer?.push({ event: eventName, value, currency: 'USD' });

    const paq = window._paq;
    if (paq) {
      if (Array.isArray(paq)) {
        paq.push(['trackEvent', 'engagement', 'session', eventName, value]);
      } else if (typeof paq.push === 'function') {
        paq.push(['trackEvent', 'engagement', 'session', eventName, value]);
      }
    }

    // GA4
    window.gtag?.('event', eventName, { event_category: 'engagement', value });
  }

  addToSessionHistory(eventName);
}

// ==================== COMPOUND EVENTS ====================
const COMPOUND_VALUES = {
  engaged_pricing_viewer: 15,
  video_to_conversion: 35,
  deep_pricing_engagement: 30,
  high_intent_session: 25,
} as const;

type CompoundEventName = keyof typeof COMPOUND_VALUES;

export function fireCompoundEvent(eventName: CompoundEventName): void {
  const value = COMPOUND_VALUES[eventName];

  if (typeof window !== 'undefined') {
    window.dataLayer?.push({ event: eventName, value, currency: 'USD', compound: true });

    const paq = window._paq;
    if (paq) {
      if (Array.isArray(paq)) {
        paq.push(['trackEvent', 'compound', eventName, '', value]);
      } else if (typeof paq.push === 'function') {
        paq.push(['trackEvent', 'compound', eventName, '', value]);
      }
    }

    // GA4
    window.gtag?.('event', eventName, { event_category: 'compound', value });
  }

  addToSessionHistory(`compound_${eventName}`);
}
